import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './core/auth.guard';
import { RoleGuard } from './core/role.guard';
import { DashboardComponent } from './features/dashboard/dashboard.component';
import { ProductsComponent } from './features/products/products.component';
import { SalesComponent } from './features/sales/sales.component';
import { CategoriesComponent } from './features/categories/categories.component';
import { LoginComponent } from './features/auth/login/login.component';

export const routes: Routes = [
  // Auth routes
  {
    path: 'login',
    component: LoginComponent,
    title: 'Login - Estokai',
  },

  // Public home page
  {
    path: 'home',
    loadComponent: () =>
      import('./features/landing/landing-page.component').then(
        (c) => c.LandingPageComponent
      ),
    title: 'Estokai - Gestão de Estoque Inteligente',
  },

  // Redirect empty path to home
  { path: '', redirectTo: '/home', pathMatch: 'full' },

  // Development/Testing routes
  {
    path: 'integration-tests',
    loadComponent: () =>
      import('./features/integration-tests/integration-tests.component').then(
        (c) => c.IntegrationTestsComponent
      ),
    title: 'Testes de Integração - Estokai',
  },

  // Main feature routes using standalone components
  {
    path: 'dashboard',
    component: DashboardComponent,
    title: 'Dashboard - Estokai',
    canActivate: [AuthGuard],
  },
  {
    path: 'products',
    component: ProductsComponent,
    title: 'Produtos - Estokai',
    canActivate: [AuthGuard],
  },
  {
    path: 'sales',
    component: SalesComponent,
    title: 'Vendas - Estokai',
    canActivate: [AuthGuard],
  },
  {
    path: 'categories',
    component: CategoriesComponent,
    canActivate: [AuthGuard],
    title: 'Categorias - Estokai',
  },

  // Coming soon routes for features not yet migrated
  {
    path: 'brands',
    loadComponent: () =>
      import('./features/brands/brands.component').then(
        (c) => c.BrandsComponent
      ),
    canActivate: [AuthGuard],
    title: 'Marcas - Estokai',
  },
  {
    path: 'customers',
    loadComponent: () =>
      import(
        './features/customers/customers-list/customers-complete.component'
      ).then((c) => c.CustomersCompleteComponent),
    canActivate: [AuthGuard],
    title: 'Clientes - Estokai',
  },
  {
    path: 'suppliers',
    loadComponent: () =>
      import('./features/suppliers/suppliers.component').then(
        (c) => c.SuppliersComponent
      ),
    canActivate: [AuthGuard],
    title: 'Fornecedores - Estokai',
  },
  {
    path: 'purchase-orders',
    loadComponent: () =>
      import('./features/purchase-orders/purchase-orders.component').then(
        (c) => c.PurchaseOrdersComponent
      ),
    title: 'Pedidos de Compra - Estokai',
  },
  {
    path: 'scanner',
    loadComponent: () =>
      import('./features/scanner/scanner.component').then(
        (c) => c.ScannerComponent
      ),
    canActivate: [AuthGuard],
    title: '📱 Scanner QR/Barcode - Estokai',
  },
  {
    path: 'reports',
    loadComponent: () =>
      import('./features/reports/reports.component').then(
        (c) => c.ReportsComponent
      ),
    canActivate: [AuthGuard],
    title: 'Relatórios - Estokai',
  },
  {
    path: 'users',
    loadComponent: () =>
      import('./features/users/users.component').then((c) => c.UsersComponent),
    canActivate: [AuthGuard],
    title: 'Usuários',
  },
  {
    path: 'inventory-locations',
    loadComponent: () =>
      import(
        './features/inventory-locations/inventory-locations.component'
      ).then((c) => c.InventoryLocationsComponent),
    canActivate: [AuthGuard],
    title: 'Localizações de Estoque - Estokai',
  },
  {
    path: 'pdv',
    loadComponent: () =>
      import('./features/pdv/pdv.component').then((c) => c.PdvComponent),
    canActivate: [AuthGuard],
    title: '🚀 PDV Inovador - Estokai',
  },
  {
    path: 'onboarding',
    loadComponent: () =>
      import('./shared/components/onboarding/onboarding.component').then(
        (c) => c.OnboardingComponent
      ),
    canActivate: [AuthGuard],
    title: '🎯 Onboarding Gamificado - Estokai',
  },
  {
    path: 'integrations',
    loadComponent: () =>
      import('./features/integrations/integrations.component').then(
        (c) => c.IntegrationsComponent
      ),
    canActivate: [AuthGuard],
    title: 'Integrações - Estokai',
  },
  {
    path: 'settings',
    loadComponent: () =>
      import('./features/settings/settings.component').then(
        (c) => c.SettingsComponent
      ),
    title: 'Configurações Gerais - Estokai',
  },
  {
    path: 'accounts-receivable',
    loadComponent: () =>
      import(
        './features/accounts-receivable/accounts-receivable.component'
      ).then((c) => c.AccountsReceivableComponent),
    canActivate: [AuthGuard],
    title: 'Contas a Receber - Estokai',
  },
  {
    path: 'accounts-receivable-reports',
    loadComponent: () =>
      import(
        './features/accounts-receivable/accounts-receivable-reports.component'
      ).then((c) => c.AccountsReceivableReportsComponent),
    canActivate: [AuthGuard],
    title: 'Relatórios de Contas a Receber - Estokai',
  },

  // Accounts Payable routes - temporary without guards for testing
  {
    path: 'accounts-payable',
    loadComponent: () =>
      import('./features/accounts-payable/accounts-payable.component').then(
        (c) => c.AccountsPayableComponent
      ),
    title: 'Contas a Pagar - Estokai',
  },
  {
    path: 'accounts-payable/new',
    loadComponent: () =>
      import(
        './features/accounts-payable/accounts-payable-form.component'
      ).then((c) => c.AccountsPayableFormComponent),
    title: 'Nova Conta a Pagar - Estokai',
  },
  {
    path: 'accounts-payable/:id',
    loadComponent: () =>
      import(
        './features/accounts-payable/accounts-payable-details.component'
      ).then((c) => c.AccountsPayableDetailsComponent),
    title: 'Detalhes da Conta a Pagar - Estokai',
  },
  {
    path: 'accounts-payable/:id/edit',
    loadComponent: () =>
      import(
        './features/accounts-payable/accounts-payable-form.component'
      ).then((c) => c.AccountsPayableFormComponent),
    title: 'Editar Conta a Pagar - Estokai',
  },
  {
    path: 'accounts-payable-reports',
    loadComponent: () =>
      import(
        './features/accounts-payable/accounts-payable-reports.component'
      ).then((c) => c.AccountsPayableReportsComponent),
    title: 'Relatórios de Contas a Pagar - Estokai',
  },

  // Cash Flow routes - temporary without guards for testing
  {
    path: 'cash-flow',
    loadComponent: () =>
      import('./features/cash-flow/cash-flow.component').then(
        (c) => c.CashFlowComponent
      ),
    title: 'Fluxo de Caixa - Estokai',
  },

  // Deep link handler
  {
    path: 'dl/:id',
    loadComponent: () =>
      import('./shared/components/deep-link/deep-link-handler.component').then(
        (c) => c.DeepLinkHandlerComponent
      ),
    title: 'Processando link...',
  },

  // Catch all route
  { path: '**', redirectTo: '/home' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
